import { DateTime } from 'luxon';
import { ITimezoneOption, ITimezoneState } from './types';

/**
 * Extracts timezone abbreviation from timezone label
 * Finds everything inside parentheses (GMT-5, EST, etc.)
 */
export const extractTimezoneAbbreviation = (label: string): string => {
	const matches = label.match(/\(([^)]+)\)/g);
	if (!matches) return '';
	
	return matches.map(match => match.replace(/[()]/g, '')).join('/');
};

/**
 * Adds abbreviation property to timezone data
 */
export const addAbbreviationToTimezones = (
	timezones: Array<{label: string; value: string}>
): ITimezoneOption[] => {
	return timezones.map((timezone) => ({
		...timezone,
		abbreviation: extractTimezoneAbbreviation(timezone.label)
	}));
};

/**
 * Gets the user's current system timezone
 */
export const getCurrentSystemTimezone = (): string => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch (error) {
		// Could not detect system timezone
		return 'America/New_York';
	}
};

/**
 * Finds timezone option by value (case insensitive)
 */
export const findTimezoneByValue = (
	options: ITimezoneOption[], 
	value: string
): ITimezoneOption | undefined => {
	return options.find(
		option => option.value.toLowerCase() === value.toLowerCase()
	);
};

/**
 * Gets default timezone state
 */
export const getDefaultTimezoneState = (options: ITimezoneOption[]): ITimezoneState => {
	const defaultOption = findTimezoneByValue(options, 'America/New_York') 
		|| options[169]; // Index for America/New_York in TIMEZONE_OPTIONS
	
	if (!defaultOption) {
		throw new Error('No default timezone option found');
	}

	return {
		timezone: defaultOption.value,
		displayName: defaultOption.label,
		abbreviation: defaultOption.abbreviation,
	};
};

/**
 * Safely parses stored timezone from localStorage
 */
export const parseStoredTimezone = (stored: string | null): ITimezoneState | null => {
	if (!stored) return null;
	
	try {
		const parsed = JSON.parse(stored);
		// Validate the structure
		if (parsed && typeof parsed === 'object' && 
			parsed.timezone && parsed.displayName && parsed.abbreviation) {
			return parsed as ITimezoneState;
		}
		return null;
	} catch (error) {
		// Failed to parse stored timezone
		return null;
	}
};

/**
 * Creates a timezone state from option
 */
export const createTimezoneState = (option: ITimezoneOption): ITimezoneState => ({
	timezone: option.value,
	displayName: option.label,
	abbreviation: option.abbreviation,
});

/**
 * Global function to convert date to selected timezone with custom formatting using Luxon
 * 
 * @param dateInput - Date input (Date object, ISO string, timestamp)
 * @param dateFormat - Luxon format pattern (e.g., 'MM/dd/yyyy', 'yyyy-MM-dd HH:mm:ss')
 * @param timezone - Target timezone (defaults to America/New_York)
 * @returns Formatted date string in the target timezone
 * 
 * @example
 * // Basic usage
 * convertDateToTimezone('2024-01-15T10:30:00Z', 'MM/dd/yyyy')
 * // Returns: "01/15/2024"
 * 
 * // With time  
 * convertDateToTimezone(new Date(), 'MM/dd/yyyy HH:mm')
 * // Returns: "01/15/2024 05:30"
 * 
 * // Luxon patterns
 * convertDateToTimezone('2024-01-15T10:30:00Z', 'MMM dd, yyyy h:mm a')  
 * // Returns: "Jan 15, 2024 5:30 AM"
 * 
 * // Custom timezone
 * convertDateToTimezone('2024-01-15', 'dd MMM yyyy', 'America/Los_Angeles')
 * // Returns: "14 Jan 2024" (if converted from UTC)
 */
export const convertDateToTimezone = (
	dateInput: string | number | Date,
	dateFormat = 'MM/dd/yyyy',
	timezone?: string,
	hideTimeZone = false,
	utcTimeZone = false
): string => {
	try {
		let dateTime: DateTime;

		// Parse different input types using Luxon
		if (typeof dateInput === 'string') {
			// Try ISO format first, then general parsing
			dateTime = DateTime.fromISO(dateInput);
			if (!dateTime.isValid) {
				dateTime = DateTime.fromJSDate(new Date(dateInput));
			}
		} else if (typeof dateInput === 'number') {
			// Handle both seconds and milliseconds timestamps
			const timestamp = dateInput.toString().length === 10 ? dateInput * 1000 : dateInput;
			dateTime = DateTime.fromMillis(timestamp);
		} else if (dateInput instanceof Date) {
			dateTime = DateTime.fromJSDate(dateInput);
		} else {
			throw new Error(`Unsupported date input type: ${typeof dateInput}`);
		}

		// Check if parsing was successful
		if (!dateTime.isValid) {
			throw new Error(`Invalid date input: ${dateInput}`);
		}

		// Set timezone and format
		const targetTimezone = timezone || 'America/New_York';
		return dateTime.setZone(utcTimeZone ? 'utc' : targetTimezone).toFormat(dateFormat + (hideTimeZone ? "" : " ZZZZ"));
		// return dateTime.setZone(targetTimezone).toFormat(dateFormat) + ' ' + abbreviation;

	} catch (error) {
		// Error will be handled by returning 'Invalid Date'
		return 'Invalid Date';
	}
};
